<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import BasicModal from '@js/Components/Modals/BasicModal.vue'
import { useMainStore } from '@js/App/Stores/MainStore'
import { BREVO_SIGNUP_MODAL_ID } from '@js/App/Plugins/Modals'
import { storeToRefs } from 'pinia'
import TextInput from '@js/Components/Forms/TextInput/TextInput.vue'
import Button from '@js/Components/Forms/Button/Button.vue'
import { useUser } from '@js/App/Api/User/User'

const STORAGE_KEY = 'brevo_popup_dismissed'
const POPUP_DELAY_MS = 5000
const BREVO_FORM_ACTION_EN = 'https://bc44c640.sibforms.com/serve/MUIFAFnSDDWbuTkyRqtZPbFk0ADNXlCp3hrOUEkW5JMIdH91baJKgAP9aCX7KjeQTbDrZlef8gvvPTRndDqemV3pGWfSg_kAnUTQFSUAWbhC8PZiTguhVp1Pq1Tiv3CM8-RLqMcljnFvH0PZhEabCX_6ZCURmmpmOiEpeHIQk8HpwbiA5zWgm48u533Rq-t9Ib06FXB1vdlxCx0i'
const BREVO_FORM_ACTION_NL = 'https://bc44c640.sibforms.com/serve/MUIFAP-7dVzDM_zTqUpctTv0jNzTHNa6DpTwrArTZxM4gXF59XPVOPo55D-i8iaEbgGsnm96YxO5JJbHqdfIjF9-6naKE5IolOQSgPA6s5z2-fTw_NHzR7b_wY-chHkm-NgfB5P8MPfiVN7M2DibfSHXpvzjhcJdQo4eEmxMrvchTSQ60V_N06M4_jUbIKwnGXAH7ZkH9-VWhkIm'

const mainStore = useMainStore()
const { fadingOutModals } = storeToRefs(mainStore)
const { userActivePayment } = useUser()
let timeoutId = null

const isPayingCustomer = computed(() => Boolean(userActivePayment.value))
const showModal = computed(() => {
  return !isPayingCustomer.value && (mainStore.openModals?.includes(BREVO_SIGNUP_MODAL_ID) ?? false)
})

const firstname = ref('')
const email = ref('')
const isSubmitting = ref(false)
const showSuccess = ref(false)
const showError = ref(false)
const firstnameError = ref('')
const emailError = ref('')

const isNl = computed(() => {
  const url = window.location.pathname
  return url.startsWith('/nl')
})

const formAction = computed(() => {
  return isNl.value ? BREVO_FORM_ACTION_NL : BREVO_FORM_ACTION_EN
})

const shouldShowOnCurrentPage = () => {
  const url = window.location.href.toLowerCase()
  const pathname = window.location.pathname.toLowerCase()

  const excludedPatterns = [
    '/checkout',
    '/create-account',
    '/maak-account',
    '/upgrade',
    '/start-trial',
    '/plus',
    '/mijn-rentbird',
    '/my-rentbird',
    '/login',
    '/logout',
    '/password',
    '/wachtwoord',
    '/relocation',
    '/house-image',
    '/u/',
    '/click'
  ]

  for (const pattern of excludedPatterns) {
    if (pathname.includes(pattern)) {
      return false
    }
  }

  if (url.includes('fromapp=true')) {
    return false
  }

  return true
}

const wasDismissed = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null
  } catch {
    return false
  }
}

const markAsDismissed = () => {
  try {
    localStorage.setItem(STORAGE_KEY, Date.now().toString())
  } catch {
  }
}

const closeModal = () => {
  mainStore.closeModal(BREVO_SIGNUP_MODAL_ID)
  markAsDismissed()
}

const hideModal = () => {
  mainStore.closeModal(BREVO_SIGNUP_MODAL_ID)
}

const shouldShowPopup = () => {
  return shouldShowOnCurrentPage() && !wasDismissed() && !isPayingCustomer.value
}

const handleSubmit = async () => {
  firstnameError.value = ''
  emailError.value = ''

  let hasErrors = false
  if (!firstname.value) {
    firstnameError.value = isNl.value ? 'Dit veld is verplicht' : 'This field is required'
    hasErrors = true
  }
  if (!email.value) {
    emailError.value = isNl.value ? 'Dit veld is verplicht' : 'This field is required'
    hasErrors = true
  }

  if (hasErrors) {
    return
  }

  isSubmitting.value = true
  showError.value = false

  try {
    const formData = new FormData()
    formData.append('FIRSTNAME', firstname.value)
    formData.append('EMAIL', email.value)
    formData.append('locale', isNl.value ? 'nl' : 'en')

    await fetch(formAction.value, {
      method: 'POST',
      body: formData,
      mode: 'no-cors'
    })

    showSuccess.value = true
    markAsDismissed()

    setTimeout(() => {
      closeModal()
    }, 2000)
  } catch {
    showError.value = true
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  if (!shouldShowPopup()) {
    return
  }

  timeoutId = setTimeout(() => {
    if (!shouldShowPopup() || (mainStore.openModals ?? []).length > 0) {
      return
    }
    mainStore.openModal(BREVO_SIGNUP_MODAL_ID)
  }, POPUP_DELAY_MS)
})

watch(isPayingCustomer, (isPaying) => {
  if (!isPaying) {
    return
  }

  if (timeoutId) {
    clearTimeout(timeoutId)
  }

  hideModal()
})

watch(
  () => mainStore.openModals,
  () => {
    const open = mainStore.openModals ?? []
    if (!open.includes(BREVO_SIGNUP_MODAL_ID) || open.length <= 1) {
      return
    }
    mainStore.closeModal(BREVO_SIGNUP_MODAL_ID)
  },
  { deep: true }
)

onUnmounted(() => {
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
})
</script>

<template>
  <BasicModal
    v-if="showModal"
    :show-top-bar="false"
    :padding="false"
    :fluid-width="true"
    :just-got-closed="fadingOutModals.includes(BREVO_SIGNUP_MODAL_ID)"
    class="brevo-modal"
    @close="closeModal"
  >
    <div class="brevo-popup-wrapper">
      <button
        class="brevo-close-btn"
        aria-label="Close"
        @click="closeModal"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div class="brevo-form-container">
        <div v-if="showError" class="brevo-message brevo-message--error">
          {{ $trans('BrevoPopup.ErrorMessage') }}
        </div>

        <div v-if="showSuccess" class="brevo-message brevo-message--success">
          {{ $trans('BrevoPopup.SuccessMessage') }}
        </div>

        <div v-if="!showSuccess" class="brevo-form-content">
          <div class="brevo-logo">
            <img
              src="https://img.mailinblue.com/7829005/images/content_library/original/693c11bf89d6542a5249b386.png"
              alt="Rentbird Logo"
              class="brevo-logo-img"
            >
          </div>

          <div class="brevo-banner">
            <img
              :src="isNl ? 'https://img.mailinblue.com/7829005/images/content_library/original/694051fb3700ceb1b805db14.png' : 'https://img.mailinblue.com/7829005/images/content_library/original/6940521a3700ceb1b805db1c.png'"
              alt=""
              class="brevo-banner-img"
            >
          </div>

          <form class="brevo-form" @submit.prevent="handleSubmit">
            <TextInput
              v-model="firstname"
              :dark="false"
              :placeholder="$trans('BrevoPopup.FirstnamePlaceholder')"
              :error="firstnameError"
              class="brevo-input"
            />

            <TextInput
              v-model="email"
              type="email"
              :dark="false"
              :placeholder="$trans('BrevoPopup.EmailPlaceholder')"
              :error="emailError"
              class="brevo-input"
            />

            <p class="brevo-email-hint">
              {{ $trans('BrevoPopup.EmailHint') }}
            </p>

            <Button
              type="submit"
              :big="true"
              :full-width-on-mobile="true"
              :full-width-on-desktop="true"
              class="brevo-submit-btn"
            >
              {{ $trans('BrevoPopup.SubmitButton') }}
            </Button>
          </form>
        </div>
      </div>
    </div>
  </BasicModal>
</template>

<style scoped>
/* BasicModal wraps content in bg-white full-screen; make it transparent so the
   same dimmed backdrop as other modals (bg-black/50 in BasicModal) shows the page behind. */
:deep(.brevo-modal) {
  background-color: transparent !important;
}

.brevo-popup-wrapper {
  position: relative;
  overflow: hidden;
  border-radius: 0;
}

.brevo-close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  z-index: 10;
}

.brevo-close-btn:hover {
  background-color: #fff;
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.brevo-close-btn svg {
  color: #374151;
}

.brevo-form-container {
  background-color: rgba(56, 47, 114, 1);
  max-width: 540px;
  padding: 24px;
}

.brevo-message {
  padding: 12px 16px;
  border-radius: 3px;
  margin-bottom: 16px;
  font-size: 14px;
}

.brevo-message--error {
  background-color: #ffeded;
  color: #661d1d;
  border: 1px solid #ff4949;
}

.brevo-message--success {
  background-color: #e7faf0;
  color: #085229;
  border: 1px solid #13ce66;
}

.brevo-form-content {
  text-align: center;
}

.brevo-logo {
  text-align: left;
  margin-bottom: 16px;
}

.brevo-logo-img {
  width: 120px;
  height: 53px;
}

.brevo-banner {
  margin-bottom: 24px;
}

.brevo-banner-img {
  max-width: 100%;
  height: auto;
}

.brevo-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.brevo-input {
  width: 100%;
}

.brevo-input :deep(input) {
  background-color: white;
  color: #1f2937;
}

.brevo-input :deep(input::placeholder) {
  color: #6b7280;
}

.brevo-email-hint {
  font-size: 12px;
  color: #ffffff;
  text-align: left;
  margin-top: -8px;
}

.brevo-submit-btn {
  margin-top: 8px;
}

@media (max-width: 600px) {
  .brevo-form-container {
    margin: 24px 8px;
    padding: 16px;
  }
}
</style>
